import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * LR HUB topic feeds (channels).
 *
 * Reads active rows from `hub_feeds` and exposes which feeds the CURRENT user
 * may POST to. Posting permissions are SERVER-enforced (moderate-hub-post +
 * a hub_posts trigger) — this hook only decides what the composer OFFERS.
 *
 * ISOLATION: hub_feeds only.
 */

export interface HubFeedChannel {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  posting_role: string;
  is_active: boolean;
  sort_order: number | null;
}

export const HUB_GENERAL_SLUG = "general";

export function useHubFeeds() {
  const { isAdmin } = useAuth();

  const query = useQuery({
    queryKey: ["hub-feeds"],
    queryFn: async (): Promise<HubFeedChannel[]> => {
      const { data, error } = await supabase
        .from("hub_feeds")
        .select("id, slug, name, description, posting_role, is_active, sort_order")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as HubFeedChannel[];
    },
    staleTime: 5 * 60_000,
  });

  const feeds = query.data ?? [];

  /** Feeds this user may create posts in. */
  const postableFeeds = useMemo(
    () => (isAdmin ? feeds : feeds.filter((f) => f.posting_role === "everyone")),
    [feeds, isAdmin],
  );

  const bySlug = useMemo(
    () => new Map(feeds.map((f) => [f.slug, f] as const)),
    [feeds],
  );

  const byId = useMemo(
    () => new Map(feeds.map((f) => [f.id, f] as const)),
    [feeds],
  );

  return {
    feeds,
    postableFeeds,
    bySlug,
    byId,
    canChooseFeed: postableFeeds.length > 1,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export default useHubFeeds;
